/**
 * The explanation, out of the way until it is asked for.
 *
 * Every card used to carry a sentence or two under its heading explaining what it was for. Each
 * one was worth saying and, together, they made every screen a wall of grey text with the actual
 * data somewhere inside it. The words are still here — one click away, on the heading they belong
 * to — so a screen reads as what it holds rather than as a description of itself.
 *
 * **Click, not hover.** A hover tooltip cannot be reached on a touch screen, disappears while you
 * are reading it, and cannot be selected or copied. This one opens on click, stays open, closes on
 * Escape or a click outside, and is focusable from the keyboard.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function InfoTip({ children, label = "What is this?" }: { children: ReactNode; label?: string }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const id = useId();

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!children) return null;

  return (
    <span ref={rootRef} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={label}
        onClick={() => setOpen((current) => !current)}
        className={`flex size-[18px] items-center justify-center rounded-full border text-[11px] font-semibold leading-none transition-colors ${
          open
            ? "border-brand-500 bg-brand-500 text-white"
            : "border-ink-300 text-ink-400 hover:border-ink-400 hover:text-ink-600"
        }`}
      >
        i
      </button>

      {open ? (
        <span
          id={id}
          role="note"
          /*
           * Left-aligned to the marker and clamped to the viewport width, because these sit at the
           * top of cards in a narrow right-hand column as often as they sit in a wide one.
           */
          className="absolute left-0 top-6 z-40 w-[min(22rem,calc(100vw-3rem))] rounded-[var(--radius-surface)] border border-ink-200 bg-white p-3 text-[13px] font-normal leading-relaxed text-ink-600"
          style={{ boxShadow: "var(--shadow-menu)" }}
        >
          {children}
        </span>
      ) : null}
    </span>
  );
}
